import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import {
    ChangeEvent,
    DragEvent,
    KeyboardEvent,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react';
import InventoryTemplateAutocomplete from '../../../components/common/InventoryTemplateAutocomplete';
import StyledDialogActions from '../../../components/common/StyledDialogActions';
import MessageUsersFormEditiorPreview from '../../../components/services/messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import useInventory, { type InventoryVersion } from '../../../hooks/useInventory';
import type { PpvMenuCategory, PpvMenuItem, UpsertPpvMenuItemPayload } from '../../../hooks/useMenu';
import useMenu from '../../../hooks/useMenu';
import { OnlyFansSubscriber } from '../../../hooks/useSubscriber';
import useSubscriberMedia from '../../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../../hooks/useSubscriberVaultAlbums';
import { UserContext } from '../../../store/UserContext';

const defaultFormState: UpsertPpvMenuItemPayload = {
    command: '',
    aliases: [],
    inventoryId: 0,
    inventorySubId: 0,
    name: '',
    message: '',
    categoryIds: [],
    previewImageKey: null,
    public: false,
    active: true,
    expireAt: null,
    boostScore: 0,
};

type PreviewUploadPhase = 'idle' | 'uploading' | 'reviewing' | 'accepted' | 'rejected';

const PREVIEW_MODERATION_REJECT_MESSAGE = "This image can't be used. Public preview images can't be explicit.";

const FieldHint = ({ children }: { children: ReactNode }) => (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {children}
    </Typography>
);

const EditorSection = ({ step, title, icon, children }: { step: number; title: string; icon: ReactNode; children: ReactNode }) => (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Typography
                component="span"
                variant="caption"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 28,
                    height: 28,
                    px: 0.75,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 700,
                    lineHeight: 1,
                }}
            >
                {step}
            </Typography>
            <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', '& .MuiSvgIcon-root': { fontSize: 26 } }}>{icon}</Box>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        </Stack>
        {children}
    </Paper>
);

interface MenuItemEditorFormProps {
    menuItem?: PpvMenuItem | null;
    subscriber: OnlyFansSubscriber;
    onSave: (menuItemId: string | null, payload: UpsertPpvMenuItemPayload) => Promise<void>;
    onCancel: () => void;
    isSaving: boolean;
}

const MenuItemEditorForm = ({ menuItem, subscriber, onSave, onCancel, isSaving }: MenuItemEditorFormProps) => {
    const userContext = useContext(UserContext);
    const theme = useTheme();
    const { menuCategoriesQuery, uploadMenuPreviewImage } = useMenu();
    const { data: contentInventory } = useInventory();
    const { getMediaCahcedReconstructedItem } = useSubscriberMedia();
    const [form, setForm] = useState<UpsertPpvMenuItemPayload>(defaultFormState);
    const [selectedInventoryVersion, setSelectedInventoryVersion] = useState<InventoryVersion | undefined>();
    const [selectedInventoryLockedMedias, setSelectedInventoryLockedMedias] = useState<OnlyFansVaultMedias.RestructuredResponseItem[]>([]);
    const [aliases, setAliases] = useState<string[]>([]);
    const [aliasesInput, setAliasesInput] = useState('');
    const [aliasesError, setAliasesError] = useState('');
    const [previewDisplayUrl, setPreviewDisplayUrl] = useState<string | null>(null);
    const [previewUploadPhase, setPreviewUploadPhase] = useState<PreviewUploadPhase>('idle');
    const [previewRejectionReason, setPreviewRejectionReason] = useState('');
    const [dropzoneActive, setDropzoneActive] = useState(false);
    const previewFileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!menuItem) {
            setForm(defaultFormState);
            setAliases([]);
            setAliasesInput('');
            setAliasesError('');
            setSelectedInventoryVersion(undefined);
            setSelectedInventoryLockedMedias([]);
            setPreviewDisplayUrl(null);
            setPreviewUploadPhase('idle');
            setPreviewRejectionReason('');
            setDropzoneActive(false);
            return;
        }

        setForm({
            command: menuItem.command,
            aliases: menuItem.aliases,
            inventoryId: menuItem.inventoryId,
            inventorySubId: menuItem.inventorySubId,
            name: menuItem.name,
            message: menuItem.message,
            categoryIds: menuItem.categoryIds,
            previewImageKey: menuItem.previewImageKey,
            public: menuItem.public,
            active: menuItem.active,
            expireAt: menuItem.expireAt,
            boostScore: typeof menuItem.boostScore === 'number' ? menuItem.boostScore : 0,
        });
        setAliases(menuItem.aliases);
        setAliasesInput('');
        setAliasesError('');
        setSelectedInventoryVersion(undefined);
        setSelectedInventoryLockedMedias([]);
        setPreviewDisplayUrl(menuItem.previewImageUrl);
        setPreviewUploadPhase('idle');
        setPreviewRejectionReason('');
        setDropzoneActive(false);
    }, [menuItem]);

    const categoryOptions: PpvMenuCategory[] = useMemo(
        () => (menuCategoriesQuery.data ?? []).filter(c => c.active),
        [menuCategoriesQuery.data],
    );

    const selectedCategoryValues: PpvMenuCategory[] = useMemo(
        () => categoryOptions.filter(c => form.categoryIds.includes(c.id)),
        [categoryOptions, form.categoryIds],
    );

    useEffect(() => {
        if (!menuItem || !contentInventory?.length || selectedInventoryVersion) {
            return;
        }

        const inventory = contentInventory.find(entry => entry.inventoryId === menuItem.inventoryId);
        const linkedVersion = inventory?.versions.find(version => version.inventorySubId === menuItem.inventorySubId);
        if (linkedVersion) {
            setSelectedInventoryVersion(linkedVersion);
        }
    }, [contentInventory, menuItem, selectedInventoryVersion]);

    const normalizedCommand = form.command.trim();
    const commandValidationError = useMemo(() => {
        if (!normalizedCommand) {
            return 'Command is required.';
        }
        if (normalizedCommand.startsWith('#')) {
            return "Do not include '#'; it is prefixed automatically.";
        }
        if (!/^[A-Za-z0-9_]+$/.test(normalizedCommand)) {
            return 'Use only letters, numbers, and underscores.';
        }
        return '';
    }, [normalizedCommand]);

    const nameValidationError = useMemo(() => {
        if (!form.name.trim()) {
            return 'Name is required.';
        }
        return '';
    }, [form.name]);

    const categoriesValidationError = useMemo(() => {
        if (!form.public) {
            return '';
        }
        const n = form.categoryIds.length;
        if (n < 1) {
            return 'Select at least one category.';
        }
        if (n > 3) {
            return 'Select at most three categories.';
        }
        return '';
    }, [form.categoryIds, form.public]);

    const expireAtValidationError = useMemo(() => {
        if (!form.expireAt) {
            return '';
        }
        const d = new Date(form.expireAt);
        if (Number.isNaN(d.getTime())) {
            return 'Invalid expiration date.';
        }
        if (d.getTime() <= Date.now()) {
            return 'Expiration must be in the future.';
        }
        return '';
    }, [form.expireAt]);

    const validateAlias = (alias: string): string => {
        if (alias.startsWith('#')) {
            return "Do not include '#'; it is prefixed automatically.";
        }
        if (!/^[A-Za-z0-9_]+$/.test(alias)) {
            return 'Use only letters, numbers, and underscores.';
        }
        if (normalizedCommand && alias.toLowerCase() === normalizedCommand.toLowerCase()) {
            return 'Alias cannot match the main command.';
        }
        return '';
    };

    const canSave = useMemo(() => {
        const baseValid =
            !commandValidationError &&
            !aliasesError &&
            !nameValidationError &&
            !expireAtValidationError &&
            form.inventoryId > 0 &&
            form.inventorySubId > 0;
        if (!baseValid) {
            return false;
        }
        if (!form.public) {
            return true;
        }
        const n = form.categoryIds.length;
        const categoriesOk = n >= 1 && n <= 3;
        const previewOk = Boolean(form.previewImageKey);
        return categoriesOk && previewOk;
    }, [
        aliasesError,
        commandValidationError,
        form.categoryIds,
        form.inventoryId,
        form.inventorySubId,
        form.previewImageKey,
        form.public,
        expireAtValidationError,
        nameValidationError,
    ]);

    const adminBoostScoreValid = useMemo(() => {
        if (!userContext.isAdmin) {
            return true;
        }
        const n = Number(form.boostScore);
        return Number.isFinite(n) && Number.isInteger(n) && n >= 0 && n <= 100;
    }, [userContext.isAdmin, form.boostScore]);

    const canSaveWithAdminBoost = canSave && adminBoostScoreValid;

    const selectedInventoryMessage = useMemo(() => selectedInventoryVersion?.text || '', [selectedInventoryVersion]);
    const previewTaggedCreators = useMemo(
        () =>
            (selectedInventoryVersion?.creatorTags ?? []).map(creator => ({
                id: creator.creatorOnlyfansId,
                name: creator.creatorName,
            })),
        [selectedInventoryVersion],
    );
    const previewPrice = useMemo(() => {
        if (!selectedInventoryVersion) return 0;
        const rawPrice =
            typeof selectedInventoryVersion.price === 'number' ? selectedInventoryVersion.price : Number(selectedInventoryVersion.price);
        return Number.isFinite(rawPrice) ? rawPrice : 0;
    }, [selectedInventoryVersion]);

    const templateSummaryLabel = useMemo(() => {
        if (!form.inventoryId || !form.inventorySubId || !contentInventory?.length) {
            return '—';
        }
        const inv = contentInventory.find(e => e.inventoryId === form.inventoryId);
        const v = inv?.versions.find(ver => ver.inventorySubId === form.inventorySubId);
        if (!inv || !v) {
            return '—';
        }
        return `${inv.name} · ${v.versionName}`;
    }, [contentInventory, form.inventoryId, form.inventorySubId]);

    useEffect(() => {
        if (!selectedInventoryVersion) {
            setSelectedInventoryLockedMedias([]);
            return;
        }

        const fetchMedias = async () => {
            const mediasPromises =
                selectedInventoryVersion.lockedMedias?.map(media => getMediaCahcedReconstructedItem(subscriber, media.mediaId)) || [];
            const medias = await Promise.all(mediasPromises).then(results => {
                return results.filter(media => media !== null) as OnlyFansVaultMedias.RestructuredResponseItem[];
            });
            medias.sort((a, b) => {
                const orderA = selectedInventoryVersion.lockedMedias?.find(m => m.mediaId === a.id)?.order ?? Number.POSITIVE_INFINITY;
                const orderB = selectedInventoryVersion.lockedMedias?.find(m => m.mediaId === b.id)?.order ?? Number.POSITIVE_INFINITY;
                return orderA - orderB;
            });
            setSelectedInventoryLockedMedias(medias);
        };

        fetchMedias();
    }, [selectedInventoryVersion]);

    const setFormField = <K extends keyof UpsertPpvMenuItemPayload>(key: K, value: UpsertPpvMenuItemPayload[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const setInventoryFromQuickSelect: Dispatch<SetStateAction<InventoryVersion | undefined>> = action => {
        setSelectedInventoryVersion(previousSelected => {
            const resolved = typeof action === 'function' ? action(previousSelected) : action;
            setForm(prev => {
                if (!resolved) return { ...prev, inventoryId: 0, inventorySubId: 0 };
                return { ...prev, inventoryId: resolved.inventoryId, inventorySubId: resolved.inventorySubId };
            });
            return resolved;
        });
    };

    const addAliasesFromInput = (input: string): boolean => {
        const tokens = input
            .split(',')
            .map(alias => alias.trim())
            .filter(Boolean);
        if (!tokens.length) return true;
        for (const token of tokens) {
            const error = validateAlias(token);
            if (error) {
                setAliasesError(`Alias "${token}" is invalid. ${error}`);
                return false;
            }
        }
        setAliases(prev => Array.from(new Set([...prev, ...tokens])));
        setAliasesError('');
        return true;
    };

    useEffect(() => {
        for (const alias of aliases) {
            const error = validateAlias(alias);
            if (error) {
                setAliasesError(`Alias "${alias}" is invalid. ${error}`);
                return;
            }
        }
        if (!aliasesInput.trim()) {
            setAliasesError('');
        }
    }, [aliases, aliasesInput, normalizedCommand]);

    const handleSave = async () => {
        const payload: UpsertPpvMenuItemPayload = {
            ...form,
            command: form.command.trim(),
            aliases,
            name: form.name.trim(),
            public: form.public,
            categoryIds: form.public ? form.categoryIds : [],
            previewImageKey: form.public ? form.previewImageKey ?? null : null,
        };
        if (userContext.isAdmin) {
            payload.boostScore = Math.min(100, Math.max(0, Math.round(Number(form.boostScore))));
        } else {
            delete payload.boostScore;
        }
        await onSave(menuItem?.id || null, payload);
    };

    const handlePublicChange = (checked: boolean) => {
        setForm(prev => ({
            ...prev,
            public: checked,
            ...(checked ? {} : { categoryIds: [], previewImageKey: null }),
        }));
        if (!checked) {
            setPreviewDisplayUrl(null);
            setPreviewUploadPhase('idle');
            setPreviewRejectionReason('');
        }
    };

    const previewBusy = previewUploadPhase === 'uploading' || previewUploadPhase === 'reviewing';
    const previewUploadLocked = previewBusy || isSaving;
    /** Approved preview URL present and not replacing via a new in-flight upload. */
    const showApprovedPreview = Boolean(previewDisplayUrl) && !previewBusy;

    const previewImageValidationError = useMemo(() => {
        if (!form.public || previewBusy) {
            return '';
        }
        if (form.previewImageKey) {
            return '';
        }
        return 'An approved preview image is required.';
    }, [form.previewImageKey, form.public, previewBusy]);

    const processPreviewFile = async (file: File) => {
        if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
            setPreviewRejectionReason('Only JPEG and PNG images are allowed.');
            setPreviewUploadPhase('rejected');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setPreviewRejectionReason('File must be 5 MB or smaller.');
            setPreviewUploadPhase('rejected');
            return;
        }
        setPreviewRejectionReason('');
        setPreviewUploadPhase('uploading');
        const result = await uploadMenuPreviewImage(file, (loaded, total) => {
            if (total > 0 && loaded >= total) {
                setPreviewUploadPhase('reviewing');
            }
        });
        if (result.ok) {
            setPreviewUploadPhase('accepted');
            setFormField('previewImageKey', result.previewImageKey);
            setPreviewDisplayUrl(result.previewImageUrl);
        } else if (result.status === 'rejected') {
            setPreviewUploadPhase('rejected');
            setPreviewRejectionReason('');
        } else {
            setPreviewUploadPhase('idle');
        }
    };

    const handlePreviewFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        await processPreviewFile(file);
    };

    const handlePreviewDropzoneDragEnter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (Array.from(event.dataTransfer.types).includes('Files')) {
            setDropzoneActive(true);
        }
    };

    const handlePreviewDropzoneDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const related = event.relatedTarget as Node | null;
        if (related && event.currentTarget.contains(related)) {
            return;
        }
        setDropzoneActive(false);
    };

    const handlePreviewDropzoneDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
    };

    const handlePreviewDropzoneDrop = async (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDropzoneActive(false);
        if (previewUploadLocked) {
            return;
        }
        const file = event.dataTransfer.files?.[0];
        if (!file) {
            return;
        }
        await processPreviewFile(file);
    };

    const handlePreviewDropzoneKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (previewUploadLocked) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            previewFileInputRef.current?.click();
        }
    };

    const clearPreviewImage = () => {
        setFormField('previewImageKey', null);
        setPreviewDisplayUrl(null);
        setPreviewUploadPhase('idle');
        setPreviewRejectionReason('');
    };

    return (
        <>
            <Stack spacing={2} sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
                <EditorSection step={1} title="Command" icon={<TerminalOutlinedIcon />}>
                    <FieldHint>
                        The hashtag fans type to trigger this item (e.g. #tease). Only letters, numbers, and underscores—no # in the field;
                        it is shown automatically.
                    </FieldHint>
                    <TextField
                        label="Fan command"
                        value={form.command}
                        onChange={e => setFormField('command', e.target.value)}
                        placeholder="tease"
                        error={!!commandValidationError}
                        helperText={commandValidationError || ' '}
                        InputProps={{ startAdornment: <InputAdornment position="start">#</InputAdornment> }}
                        fullWidth
                    />
                    <FieldHint>
                        Optional alternate hashtags (languages, misspellings). Type a word and press comma or Enter to add a chip.
                    </FieldHint>
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={aliases}
                        inputValue={aliasesInput}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => <Chip {...getTagProps({ index })} key={option} label={option} size="small" />)
                        }
                        onInputChange={(_, value, reason) => {
                            if (reason !== 'input') return;
                            setAliasesInput(value);
                            const draft = value.trim();
                            if (!draft) {
                                setAliasesError('');
                                return;
                            }
                            const draftToken = draft.split(',').pop()?.trim() ?? '';
                            if (!draftToken) {
                                setAliasesError('');
                                return;
                            }
                            const draftError = validateAlias(draftToken);
                            setAliasesError(draftError ? `Alias "${draftToken}" is invalid. ${draftError}` : '');
                        }}
                        onChange={(_, value) => {
                            const nextAliases = value
                                .flatMap(alias => alias.split(','))
                                .map(alias => alias.trim())
                                .filter(Boolean);
                            for (const alias of nextAliases) {
                                const error = validateAlias(alias);
                                if (error) {
                                    setAliasesError(`Alias "${alias}" is invalid. ${error}`);
                                    return;
                                }
                            }
                            setAliases(Array.from(new Set(nextAliases)));
                            setAliasesError('');
                        }}
                        onBlur={() => {
                            if (!aliasesInput.trim()) return;
                            const added = addAliasesFromInput(aliasesInput);
                            if (added) setAliasesInput('');
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Alternate commands"
                                error={!!aliasesError}
                                helperText={aliasesError || ''}
                                onKeyDown={event => {
                                    if (event.key === ',' || event.key === 'Enter') {
                                        event.preventDefault();
                                        const added = addAliasesFromInput(aliasesInput);
                                        if (added) setAliasesInput('');
                                    }
                                }}
                            />
                        )}
                        fullWidth
                    />
                </EditorSection>

                <EditorSection step={2} title="Fan-facing menu item" icon={<LocalOfferOutlinedIcon />}>
                    <FieldHint>A short name and description shown in your menu listing.</FieldHint>
                    <TextField
                        label="Menu item name"
                        value={form.name}
                        onChange={e => setFormField('name', e.target.value)}
                        error={!!nameValidationError}
                        helperText={nameValidationError || ' '}
                        fullWidth
                    />
                    <TextField
                        label="Short description"
                        value={form.message}
                        onChange={e => setFormField('message', e.target.value)}
                        fullWidth
                        multiline
                        minRows={4}
                        helperText={`${(form.message || '').length} / 160 · Shown in the public menu, not the locked message body.`}
                    />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <MobileDateTimePicker
                            label="Expires at (optional)"
                            value={form.expireAt ? moment(form.expireAt) : null}
                            onChange={value => {
                                if (!value || !value.isValid()) {
                                    setFormField('expireAt', null);
                                    return;
                                }
                                setFormField('expireAt', value.toISOString());
                            }}
                            disablePast
                            minDateTime={moment().add(1, 'minute')}
                            ampm={true}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!expireAtValidationError,
                                    helperText: expireAtValidationError || 'Leave empty to keep this command active indefinitely.',
                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EventOutlinedIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: form.expireAt ? (
                                            <InputAdornment position="end">
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    onMouseDown={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setFormField('expireAt', null);
                                                    }}
                                                    disabled={isSaving}
                                                >
                                                    Clear
                                                </Button>
                                            </InputAdornment>
                                        ) : undefined,
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </EditorSection>

                <EditorSection step={3} title="Message to send" icon={<SendOutlinedIcon />}>
                    <FieldHint>
                        Content template used when a fan runs this command. Price, media, tags, and message text come from the template.
                    </FieldHint>
                    <InventoryTemplateAutocomplete
                        setSelectedInventoryVersion={setInventoryFromQuickSelect}
                        value={
                            form.inventoryId > 0 && form.inventorySubId > 0
                                ? { inventoryId: form.inventoryId, inventorySubId: form.inventorySubId }
                                : null
                        }
                    />
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Message preview
                    </Typography>
                    <MessageUsersFormEditiorPreview
                        message={selectedInventoryMessage}
                        taggedCreators={previewTaggedCreators}
                        taggedReleaseForms={[]}
                        mediaFiles={selectedInventoryLockedMedias}
                        mediaFilesPreviews={selectedInventoryVersion?.unlockedMedias?.map(media => media.mediaId) || []}
                        lockMessage={previewPrice > 0}
                        lockMessagePrice={previewPrice}
                        lockedText={false}
                        theme={theme}
                        fullWidth
                        showTags
                        showTime
                        showMedia
                    />
                </EditorSection>

                <EditorSection step={4} title="Public discovery" icon={<PublicOutlinedIcon />}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.public}
                                onChange={e => handlePublicChange(e.target.checked)}
                                color="primary"
                                disabled={isSaving}
                            />
                        }
                        label={
                            <Typography component="span" variant="body2">
                                List this command publicly on{' '}
                                <Link
                                    href="https://onlypromote.me"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{ fontWeight: 600 }}
                                >
                                    OnlyPromote.Me
                                </Link>
                            </Typography>
                        }
                    />
                    <input
                        ref={previewFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: 'none' }}
                        onChange={handlePreviewFileChange}
                    />
                    <Collapse in={form.public} timeout="auto" unmountOnExit>
                        <Stack spacing={2} sx={{ pt: 2 }}>
                            <Box>
                                <FieldHint>Pick 1–3 categories so fans can find your listing on OnlyPromote.Me.</FieldHint>
                                <Autocomplete
                                    multiple
                                    loading={menuCategoriesQuery.isLoading}
                                    options={categoryOptions}
                                    value={selectedCategoryValues}
                                    onChange={(_, value) => {
                                        if (value.length <= 3) {
                                            setFormField(
                                                'categoryIds',
                                                value.map(c => c.id),
                                            );
                                        }
                                    }}
                                    getOptionLabel={option => option.name}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.name} />
                                        ))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Categories"
                                            placeholder="Search categories"
                                            error={!!categoriesValidationError}
                                            helperText={categoriesValidationError}
                                        />
                                    )}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Public preview image
                                </Typography>
                                <Stack spacing={1.5}>
                                    {showApprovedPreview ? (
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                display: 'inline-block',
                                                maxWidth: '100%',
                                                alignSelf: 'flex-start',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={previewDisplayUrl!}
                                                alt="Menu preview"
                                                sx={{
                                                    width: 'auto',
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    maxHeight: 280,
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                    borderRadius: 1,
                                                    border: 1,
                                                    borderColor: 'divider',
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                aria-label="Remove preview image"
                                                onClick={clearPreviewImage}
                                                disabled={isSaving}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    color: 'common.white',
                                                    bgcolor: 'rgba(0, 0, 0, 0.45)',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0, 0, 0, 0.62)',
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: 'action.disabled',
                                                        bgcolor: 'action.disabledBackground',
                                                    },
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box
                                                role="button"
                                                tabIndex={previewUploadLocked ? -1 : 0}
                                                aria-label="Upload preview image. Drop a file or click to browse."
                                                aria-disabled={previewUploadLocked}
                                                onClick={() => {
                                                    if (!previewUploadLocked) {
                                                        previewFileInputRef.current?.click();
                                                    }
                                                }}
                                                onKeyDown={handlePreviewDropzoneKeyDown}
                                                onDragEnter={handlePreviewDropzoneDragEnter}
                                                onDragLeave={handlePreviewDropzoneDragLeave}
                                                onDragOver={handlePreviewDropzoneDragOver}
                                                onDrop={handlePreviewDropzoneDrop}
                                                sx={{
                                                    width: '100%',
                                                    py: 2.5,
                                                    px: 2,
                                                    border: '2px dashed',
                                                    borderColor: dropzoneActive ? 'primary.main' : 'divider',
                                                    bgcolor: dropzoneActive ? 'action.hover' : 'background.default',
                                                    cursor: previewUploadLocked ? 'not-allowed' : 'pointer',
                                                    opacity: previewUploadLocked ? 0.6 : 1,
                                                    textAlign: 'center',
                                                    transition: theme.transitions.create(['border-color', 'background-color'], {
                                                        duration: theme.transitions.duration.shortest,
                                                    }),
                                                    '&:focus-visible': {
                                                        outline: `2px solid ${theme.palette.primary.main}`,
                                                        outlineOffset: 2,
                                                    },
                                                }}
                                            >
                                                <CloudUploadIcon
                                                    color={dropzoneActive ? 'primary' : 'action'}
                                                    sx={{ fontSize: 40, mb: 1, opacity: previewUploadLocked ? 0.5 : 1 }}
                                                />
                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                                                    Upload image
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                    JPG or PNG (max 5 MB)
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                                    Or drop a file here
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    display="block"
                                                    sx={{ mt: 1, fontWeight: 600 }}
                                                >
                                                    Images with nudity, sexual content, or graphic violence will be rejected.
                                                </Typography>
                                            </Box>
                                            {(previewUploadPhase === 'uploading' || previewUploadPhase === 'reviewing') && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                        {previewUploadPhase === 'uploading' ? 'Uploading…' : 'Reviewing image…'}
                                                    </Typography>
                                                    <LinearProgress />
                                                </Box>
                                            )}
                                        </>
                                    )}
                                    {/* {previewUploadPhase === 'accepted' && showApprovedPreview && (
                                        <Alert severity="success" onClose={() => setPreviewUploadPhase('idle')}>
                                            Image accepted. Save the menu item to keep it.
                                        </Alert>
                                    )} */}
                                    {previewUploadPhase === 'rejected' && (
                                        <Alert severity="error" onClose={() => setPreviewUploadPhase('idle')}>
                                            {previewRejectionReason || PREVIEW_MODERATION_REJECT_MESSAGE}
                                        </Alert>
                                    )}
                                    <FormHelperText error={!!previewImageValidationError} sx={{ mx: 0 }}>
                                        {previewImageValidationError}
                                    </FormHelperText>
                                    <Alert
                                        severity="success"
                                        variant="outlined"
                                        icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                                        sx={{
                                            alignItems: 'flex-start',
                                            bgcolor: t =>
                                                t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.08)',
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Tips for success
                                        </Typography>
                                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25, mb: 0 }}>
                                            <Typography component="li" variant="body2">
                                                Keep the short description concise—fans scan listings quickly.
                                            </Typography>
                                            <Typography component="li" variant="body2">
                                                Pick a template that matches what you promised in the teaser text.
                                            </Typography>
                                            <Typography component="li" variant="body2">
                                                Use a clean, tasteful preview image; avoid anything you would not want indexed publicly.
                                            </Typography>
                                        </Stack>
                                    </Alert>
                                </Stack>
                            </Box>
                        </Stack>
                    </Collapse>
                    {/* <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', bgcolor: 'action.hover', mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Command summary
                        </Typography>
                        <Stack spacing={0.75}>
                            <Typography variant="body2">
                                <Box component="span" color="text.secondary">
                                    Command{' '}
                                </Box>
                                {normalizedCommand ? `#${normalizedCommand}` : '—'}
                            </Typography>
                            <Typography variant="body2">
                                <Box component="span" color="text.secondary">
                                    Name{' '}
                                </Box>
                                {form.name.trim() || '—'}
                            </Typography>
                            <Typography variant="body2">
                                <Box component="span" color="text.secondary">
                                    Template{' '}
                                </Box>
                                {templateSummaryLabel}
                            </Typography>
                        </Stack>
                    </Paper> */}
                    {!form.public && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Turn on public listing to choose categories and upload a preview image for OnlyPromote.Me.
                        </Typography>
                    )}
                </EditorSection>

                {userContext.isAdmin && (
                    <EditorSection step={5} title="OnlyPromote directory boost" icon={<SpeedOutlinedIcon />}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Admin only.</strong> Boost score is an integer from 0 to 100 (default 0). It is stored on the menu item,
                            included in the OnlyPromote.me API for ranking, and hidden from the subscriber dashboard unless the signed-in
                            user is an admin.
                        </Alert>
                        <TextField
                            label="Boost score"
                            type="number"
                            value={form.boostScore ?? 0}
                            onChange={e => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    setFormField('boostScore', 0);
                                    return;
                                }
                                const n = Number.parseInt(raw, 10);
                                if (Number.isNaN(n)) {
                                    return;
                                }
                                setFormField('boostScore', Math.min(100, Math.max(0, n)));
                            }}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            disabled={isSaving}
                            error={!adminBoostScoreValid}
                            helperText={
                                adminBoostScoreValid
                                    ? 'Higher values can prioritize this item in directory-style listings on onlypromote.me.'
                                    : 'Enter a whole number from 0 to 100.'
                            }
                            fullWidth
                        />
                    </EditorSection>
                )}
            </Stack>

            <StyledDialogActions>
                <Button variant="outlined" color="inherit" onClick={onCancel} disabled={isSaving} size="medium">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!canSaveWithAdminBoost || isSaving}
                    onClick={handleSave}
                    size="medium"
                    sx={{ minWidth: { sm: 160 } }}
                >
                    {isSaving ? <CircularProgress size={22} color="inherit" /> : menuItem ? 'Save changes' : 'Create command'}
                </Button>
            </StyledDialogActions>
        </>
    );
};

export default MenuItemEditorForm;
